import { connect } from 'react-redux';
import { getApplicationStatus } from '../../../api/lendingAPI/ApplicationSelector';
import { BankAccountCard } from '../cards/BankAccountCard';
import { CreateNewCard } from '../cards/CreateNewCard';
import PaymentCard from '../cards/PaymentCard';
import { INITIALISE_PAYMENT_METHOD_DEBIT_CARD } from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { FormattedMessage } from 'react-intl';
import messages from './PaymentDetails.messages';
import CollapsibleSection from '../../ui/CollapsibleSection/CollapsibleSection';
import LinkDebitCard from '../../modals/addPaymentMethodModal/linkDebitCard/LinkDebitCard';
import LinkBankAccount from '../../modals/addPaymentMethodModal/linkBankAccount/LinkBankAccount';
import RepaymentCard from '../cards/RepaymentCard';
import { Card, H2, Text } from '@ui';
import profileMessages from '../Profile.messages';
import { Dispatch } from 'redux';
import { RootState } from '@iwProvider/configureStore';
import { deserializePaymentMethod } from '../ProfileHelper';
import { UPDATE_BANK_ACCOUNT } from '../../modals/editPaymentMethodModal/EditPaymentMethodModal.actions';
import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { GetContextResponse } from '@iwoca/lapi-client/edge';

const mapStateToProps = (state: RootState) => ({
    // @ts-expect-error
    ...state.profile,
    hasDecision: getApplicationStatus(state) !== 'open'
});

type TPaymentMethod = ReturnType<typeof deserializePaymentMethod>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showAddDebitCardFormCallback: () =>
        dispatch({
            type: INITIALISE_PAYMENT_METHOD_DEBIT_CARD
        }),
    updateBankAccount: () =>
        dispatch({
            type: UPDATE_BANK_ACCOUNT
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const isBlockedIwocaPaySeller = (
    context: GetContextResponse['data']
): boolean => {
    /*
        iwocaPay Sellers are prevented from updating their bank details.
        It must go through ops
    */
    if (context?.groups?.includes('bank_details_can_modify')) return false;

    //@ts-expect-error schema not updated
    return !!context?.is_iwocapay_seller;
};

const PaymentDetails = ({
    paymentMethods,
    showAddBankAccountForm,
    showAddDebitCardForm,
    showAddDebitCardFormCallback,
    hasDecision,
    updateBankAccount
}: PropsFromRedux) => {
    const paymentCards = paymentMethods.filter(
        (paymentMethod: TPaymentMethod) => paymentMethod.type === 'card'
    );

    const bankAccount = paymentMethods.find(
        (paymentMethod: TPaymentMethod) => paymentMethod.type === 'bank_account'
    );

    return (
        <div className="space-y-m">
            <div>
                <H2>
                    <FormattedMessage
                        {...profileMessages.paymentDetailsTitle}
                    />
                </H2>
                <Text className="mt-xs">
                    <FormattedMessage
                        {...profileMessages.paymentDetailsSubtitle}
                    />
                </Text>
                {getCountry() === 'DE' && (
                    <Text className="mt-xs">
                        <FormattedMessage
                            {...profileMessages.paymentDetailsAnotherSubtitle}
                        />
                    </Text>
                )}
            </div>
            <div className="block grid-cols-2 gap-l sm:grid lg:grid-cols-3">
                <RepaymentAccountDetails
                    showAddBankAccountForm={showAddBankAccountForm}
                    bankAccount={bankAccount}
                    updateBankAccount={updateBankAccount}
                />
            </div>
            <section className="block grid-cols-2 gap-l space-y-m sm:grid sm:space-y-0 lg:grid-cols-3">
                {paymentCards.map((paymentMethod: TPaymentMethod) => (
                    <PaymentCard
                        {...paymentMethod}
                        key={paymentMethod.paymentMethodID}
                    />
                ))}
                <AddNewPaymentMethod
                    showAddBankAccountForm={showAddBankAccountForm}
                    showAddDebitCardForm={showAddDebitCardForm}
                    handleClick={showAddDebitCardFormCallback}
                />
                <NewBankAccountForm
                    hasDecision={hasDecision}
                    showAddBankAccountForm={showAddBankAccountForm}
                />
            </section>

            <NewDebitCardForm showAddDebitCardForm={showAddDebitCardForm} />
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(PaymentDetails);

const RepaymentAccountDetails = ({
    showAddBankAccountForm,
    bankAccount,
    updateBankAccount
}: {
    showAddBankAccountForm: boolean;
    bankAccount: TPaymentMethod;
    updateBankAccount: () => void;
}) => {
    if (showAddBankAccountForm) return null;

    return (
        <>
            <BankAccountCard
                {...bankAccount}
                updateBankAccount={() => updateBankAccount()}
            />
            <RepaymentCard />
        </>
    );
};

const NewBankAccountForm = ({
    hasDecision,
    showAddBankAccountForm
}: {
    hasDecision: boolean;
    showAddBankAccountForm: boolean;
}) => {
    const { data } = useGetContext();
    if (getCountry() === 'DE' && !hasDecision) return null;

    if (!showAddBankAccountForm) return null;

    if (isBlockedIwocaPaySeller(data)) {
        return (
            <Card>
                <p>Please contact your account manager to add bank details.</p>
            </Card>
        );
    }

    return (
        <div className="col-span-2">
            <CollapsibleSection
                title={<FormattedMessage {...messages.addBankAccount} />}
                isCollapsible={false}
            >
                <LinkBankAccount showTitle={false} />
            </CollapsibleSection>
        </div>
    );
};

const NewDebitCardForm = ({
    showAddDebitCardForm
}: {
    showAddDebitCardForm: boolean;
}) => {
    if (getCountry() === 'DE') return null;

    if (!showAddDebitCardForm) return null;

    return (
        <CollapsibleSection
            title={<FormattedMessage {...messages.addDebitCard} />}
            isCollapsible={false}
        >
            <LinkDebitCard
                showTitle={false}
                showSubText={false}
                showDefaultPaymentMethodCheckbox={true}
            />
        </CollapsibleSection>
    );
};

const AddNewPaymentMethod = ({
    showAddBankAccountForm,
    showAddDebitCardForm,
    handleClick
}: {
    showAddBankAccountForm: boolean;
    showAddDebitCardForm: boolean;
    handleClick: () => void;
}) => {
    if (getCountry() === 'DE' || showAddBankAccountForm || showAddDebitCardForm)
        return null;

    return (
        <CreateNewCard
            label={<FormattedMessage {...messages.addDebitCard} />}
            onClick={handleClick}
        />
    );
};
